import _, { uniqueId } from 'lodash';
import { Branches, branchesWithAlwaysSeparativePayments } from '../../../../common/model/account/branches';
import { AccountServicesWithChargesViewModel } from '../../../../common/model/account/accountServicesWithChargesViewModel';
import { AccountBalanceViewModel } from '../../../../common/model/account/accountBalanceViewModel';
import { DebtTypeFromBilling } from '../../../../common/model/account/debtType';
import { AccountWithBalanceAndChargesViewModel } from '../../../../common/model/account/accountWithBalanceAndChargesViewModel';
import { AccountServiceChargeDetailViewModel } from '../../../../common/model/account/accountServiceChargeDetailViewModel';
import { AccountServiceChargeViewModel } from '../../../../common/model/account/accountServiceChargeViewModel';
import { ServicesViewModel } from '../../../../common/model/account/servicesViewModel';

export interface ChargeDetails {
    id?: string;
    accountServiceChargeId?: string;
    volume: string;
    tariffName?: string;
    tariffPrice?: number | string;
    tariffZone?: string;
    chargeAmount?: number;
    recalcAmount?: number;
    chargeType?: string;
}

export type BalanceAndChargesServiceMappedData = Required<
    Pick<ServicesViewModel, 'name' | 'parentalServiceId' | 'id' | 'hasTariff' | 'prepaymentAllowed' | 'priority'> & // Поставщик / Услуга
    Pick<AccountServicesWithChargesViewModel, 'branch'> &
    Pick<
        AccountServiceChargeViewModel,
        'accountServiceId' // id услуги ЛС
        | 'beginPeriodBalance' // Сальдо ?
        | 'paymentsAmount' // Оплачено,₽
        | 'endPeriodBalance' // К оплате,₽
        | 'debtType' // Тип долга
        | 'excessPay'
        | 'increaseCoefficient'
    > & Pick<
        AccountServiceChargeDetailViewModel,
        'chargeAmount' | // Начислено
        'recalcAmount' | // Перерасчет,₽
        'tariffPrice' // Тариф
    > & {
        chargeDetails: ChargeDetails[];
        isAtomic: boolean;
        codeGroup: number | undefined;
        contragentPriority: number;
        paymentDisabled: boolean;
        periodMonth: number;
        periodYear: number;
        isChargeDecoding: boolean;
    }
>;

export type BalanceAndChargesServicesDictionary = {
    [serviceProvider: string]: BalanceAndChargesServiceMappedData[];
};

export interface BalanceAndChargesMappedDataItem {
    accountId: AccountWithBalanceAndChargesViewModel['id'];
    isSeparatePayments: AccountWithBalanceAndChargesViewModel['isSeparatePayments'];
    balance: AccountBalanceViewModel;
    allServices: BalanceAndChargesServicesDictionary;
    atomicServices: BalanceAndChargesServicesDictionary;
    separatableServices: BalanceAndChargesServicesDictionary;
}

export type BalanceAndChargesMappedData = Partial<Record<DebtTypeFromBilling, BalanceAndChargesMappedDataItem>>;

export type BalanceAndChargesItemMappedData = NonNullable<BalanceAndChargesMappedData[keyof BalanceAndChargesMappedData]>;
export type AccountWithMappedBalanceAndChargesData = AccountWithBalanceAndChargesViewModel & {
    mappedData: BalanceAndChargesMappedData;
    // services: AccountWithBalanceAndChargesViewModel['services'];
};
export type GroupedAccountWithMappedBalanceAndChargesData = AccountWithMappedBalanceAndChargesData & {
    groupedData: Record<number, AccountWithMappedBalanceAndChargesData>;
}

const getTransformedVolume = (hasTariff: boolean, volume?: number, measure?: string) => {
    if (!hasTariff) {
        return volume ? `${`${volume}`.replace('.', ',')}${measure ? `\u00a0${measure}` : ''}` : '';
    }

    return volume ? `${`${volume}`.replace('.', ',')}${measure ? `\u00a0${measure}` : ''}` : '-';
};

const getTransformedTariffPrice = (hasTariff: boolean, tariffPrice?: number) => {
    return (hasTariff ? (tariffPrice ?? 0) : tariffPrice) || '';
};

const mapAccountServiceChargeDetails = (charge: AccountServiceChargeViewModel, chargeDetails: AccountServiceChargeDetailViewModel[], measure: string, hasTariff: boolean): BalanceAndChargesServiceMappedData['chargeDetails'] => {
    return chargeDetails.map(chargeDetail => {
        const {
            id,
            accountServiceChargeId,
            volume,
            tariffName,
            tariffPrice,
            chargeAmount,
            recalcAmount,
            tariffZone,
        } = chargeDetail;

        const displayTariffCell = hasTariff || (tariffPrice && volume);

        return {
            id,
            accountServiceChargeId,
            volume: displayTariffCell ? getTransformedVolume(hasTariff, volume, measure) : '',
            tariffName,
            tariffPrice: displayTariffCell ? getTransformedTariffPrice(hasTariff, tariffPrice) : '',
            tariffZone,
            chargeAmount,
            recalcAmount,
            chargeType: charge.chargeType,
        };
    });
};

const SERVICE_PRIORITY_DEFAULT_NULL_VALUE = 1_000_000;

export const accountBalanceAndChargesDataMapper = (
    account: AccountWithBalanceAndChargesViewModel
): GroupedAccountWithMappedBalanceAndChargesData => {
    const mappedData: BalanceAndChargesMappedData = {
        [DebtTypeFromBilling.Main]: {
            accountId: account.id,
            isSeparatePayments: account.isSeparatePayments,
            balance: {
                beginPeriodBalance: 0,
                chargeAmount: 0,
                paymentsAmount: 0,
                recalcAmount: 0,
                endPeriodBalance: 0,
                periodYear: 0,
                periodMonth: 0,
            },
            allServices: {},
            atomicServices: {},
            separatableServices: {},
        }
    };

    const groupedData: Record<number, AccountWithMappedBalanceAndChargesData> = {};

    const { services, ...dataToClone } = account;

    for (const service of services ?? []) {
        const providerName = service?.performer?.name || service?.provider?.name;
        const isChargeDecoding = Boolean(service?.isChargeDecoding);

        if (!providerName || !service.accountServiceCharges?.length) {
            continue;
        }

        for (const serviceCharge of (service.accountServiceCharges as Required<AccountServiceChargeViewModel>[])) {
            if (!serviceCharge.debtType) {
                continue;
            }

            const isServiceFromAtomicGroup = !account.isSeparatePayments && serviceCharge.debtType !== DebtTypeFromBilling.Other && (
                !service.branch || !branchesWithAlwaysSeparativePayments.includes(service.branch)
            );

            const servicesGroupProp = isServiceFromAtomicGroup ? 'atomicServices' : 'separatableServices';
            const existedServicesMappedData =
                mappedData[DebtTypeFromBilling.Main]?.[servicesGroupProp] as BalanceAndChargesServicesDictionary ?? {};
            const existedBalance = mappedData[DebtTypeFromBilling.Main]?.balance as Required<AccountBalanceViewModel>;

            if (!existedServicesMappedData[providerName]) {
                existedServicesMappedData[providerName] = [];
            }

            const servicesAllMappedData = mappedData[DebtTypeFromBilling.Main]?.allServices ?? {};
            if (!servicesAllMappedData[providerName]) {
                servicesAllMappedData[providerName] = [];
            }

            const servicesGroupMappedData = existedServicesMappedData[providerName];

            const paymentDisabled =
                !!(service.service?.paymentDisabled || serviceCharge.isPayBlocked);

            const existedServiceMappedData = servicesGroupMappedData.find(
                s =>
                    s.accountServiceId === serviceCharge.accountServiceId &&
                    s.paymentDisabled === paymentDisabled
            );

            const extendServiceMappedDataFromChargeDetails = (
                serviceMappedData: BalanceAndChargesServiceMappedData
            ) => {
                const isBlocked =
                    !!(service.service?.paymentDisabled || serviceCharge.isPayBlocked);

                existedBalance.periodYear = serviceCharge.periodYear;
                existedBalance.periodMonth = serviceCharge.periodMonth;

                if (!isBlocked) {
                    existedBalance.beginPeriodBalance += serviceCharge.beginPeriodBalance ?? 0;
                    existedBalance.paymentsAmount += serviceCharge.paymentsAmount ?? 0;
                    existedBalance.endPeriodBalance += serviceCharge.endPeriodBalance ?? 0;
                    existedBalance.recalcAmount += serviceCharge.recalcAmount;

                    for (const chargeDetail of serviceCharge.accountServiceChargeDetails) {
                        existedBalance.chargeAmount += chargeDetail.chargeAmount ?? 0;
                    }
                }

                for (const chargeDetail of serviceCharge.accountServiceChargeDetails as Required<AccountServiceChargeDetailViewModel>[]) {
                    serviceMappedData.chargeAmount += chargeDetail.chargeAmount;
                    serviceMappedData.tariffPrice += chargeDetail.tariffPrice;
                }
            };

            const measure = service?.accountServiceCharges[0]?.measure || '';

            if (existedServiceMappedData) {
                existedServiceMappedData.beginPeriodBalance += serviceCharge.beginPeriodBalance;
                existedServiceMappedData.paymentsAmount += serviceCharge.paymentsAmount;
                existedServiceMappedData.endPeriodBalance += serviceCharge.endPeriodBalance;
                existedServiceMappedData.chargeDetails.push(
                    ...mapAccountServiceChargeDetails(
                        serviceCharge, serviceCharge.accountServiceChargeDetails, measure, !!service.service?.hasTariff
                    )
                );

                extendServiceMappedDataFromChargeDetails(existedServiceMappedData);
            } else {
                const isPaymentDisabled = !!(service.service?.paymentDisabled || serviceCharge?.isPayBlocked);

                const newServiceMappedData: BalanceAndChargesServiceMappedData = {
                    id: service.service?.id ?? '',
                    name: `${service.service?.name}` || '(Неизвестная услуга)',
                    parentalServiceId: service.service?.parentalServiceId ?? '',
                    branch: service.branch as Branches,
                    accountServiceId: !isPaymentDisabled ? serviceCharge.accountServiceId : uniqueId('service-'),
                    beginPeriodBalance: serviceCharge.beginPeriodBalance,
                    paymentsAmount: serviceCharge.paymentsAmount,
                    endPeriodBalance: serviceCharge.endPeriodBalance,
                    chargeDetails: mapAccountServiceChargeDetails(
                        serviceCharge, serviceCharge.accountServiceChargeDetails, measure, !!service.service?.hasTariff
                    ),
                    chargeAmount: 0,
                    recalcAmount: serviceCharge.recalcAmount,
                    tariffPrice: 0,
                    debtType: serviceCharge.debtType,
                    hasTariff: !!service.service?.hasTariff,
                    excessPay: serviceCharge.excessPay,
                    increaseCoefficient: serviceCharge.increaseCoefficient,
                    // #2162 если флаг опциональный, тогда считаем что по умолчанию можно платить
                    prepaymentAllowed: !account?.isDisabled && (service.service?.prepaymentAllowed ?? true),
                    isAtomic: isServiceFromAtomicGroup,
                    codeGroup: !!service.performer ? service.performer?.codeGroup : service.provider?.codeGroup,
                    priority: service.service?.priority ?? SERVICE_PRIORITY_DEFAULT_NULL_VALUE,
                    contragentPriority: service.contragent?.priority ?? 0,
                    paymentDisabled: isPaymentDisabled,
                    periodMonth: serviceCharge.periodMonth,
                    periodYear: serviceCharge.periodYear,
                    isChargeDecoding,
                };

                extendServiceMappedDataFromChargeDetails(newServiceMappedData);

                servicesGroupMappedData.push(newServiceMappedData);
                servicesAllMappedData[providerName].push(newServiceMappedData);
            }
        }
    }

    // инициализируем структуру groupedData
    for (const service of services ?? []) {
        const providerName = service?.performer?.name || service?.provider?.name;

        if (!providerName || !service.accountServiceCharges?.length) {
            continue;
        }

        const codeGroup = (!!service?.performer ? service?.performer?.codeGroup : service?.provider?.codeGroup) ?? 0;

        const mappedDataItem: BalanceAndChargesMappedData = {
            [DebtTypeFromBilling.Main]: {
                accountId: account.id,
                isSeparatePayments: account.isSeparatePayments,
                balance: {
                    beginPeriodBalance: 0,
                    chargeAmount: 0,
                    paymentsAmount: 0,
                    recalcAmount: 0,
                    endPeriodBalance: 0,
                    periodYear: 0,
                    periodMonth: 0,
                },
                allServices: {},
                atomicServices: {},
                separatableServices: {},
            }
        };

        if (!groupedData[codeGroup]) {
            groupedData[codeGroup] = {
                ..._.clone(dataToClone),
                mappedData: mappedDataItem,
                services: []
            };
        }

        groupedData[codeGroup].services?.push(service);
    }

    for (const codeGroupIndex of Object.keys(groupedData)) {
        const codeGroup = Number(codeGroupIndex);
        for (const service of groupedData[codeGroup].services ?? []) {
            const providerName = service?.performer?.name || service?.provider?.name;
            const isChargeDecoding = Boolean(service?.isChargeDecoding);

            if (!providerName || !service.accountServiceCharges?.length) {
                continue;
            }

            for (const serviceCharge of (service.accountServiceCharges as Required<AccountServiceChargeViewModel>[])) {
                if (!serviceCharge.debtType) {
                    continue;
                }

                const isServiceFromAtomicGroup = !account.isSeparatePayments && serviceCharge.debtType !== DebtTypeFromBilling.Other && (
                    !service.branch || !branchesWithAlwaysSeparativePayments.includes(service.branch)
                );

                const servicesGroupProp = isServiceFromAtomicGroup ? 'atomicServices' : 'separatableServices';
                const existedServicesMappedData = groupedData[codeGroup]
                    .mappedData[DebtTypeFromBilling.Main]?.[servicesGroupProp] as BalanceAndChargesServicesDictionary ?? {};
                const existedBalance = groupedData[codeGroup]
                    .mappedData[DebtTypeFromBilling.Main]?.balance as Required<AccountBalanceViewModel>;

                if (!existedServicesMappedData[providerName]) {
                    existedServicesMappedData[providerName] = [];
                }

                const servicesAllMappedData = groupedData[codeGroup].mappedData[DebtTypeFromBilling.Main]?.allServices ?? {};
                if (!servicesAllMappedData[providerName]) {
                    servicesAllMappedData[providerName] = [];
                }

                const servicesGroupMappedData = existedServicesMappedData[providerName];

                const paymentDisabled =
                    !!(service.service?.paymentDisabled || serviceCharge.isPayBlocked);

                const existedServiceMappedData = servicesGroupMappedData.find(
                    s =>
                        s.accountServiceId === serviceCharge.accountServiceId &&
                        s.paymentDisabled === paymentDisabled
                );

                const extendServiceMappedDataFromChargeDetails = (
                    serviceMappedData: BalanceAndChargesServiceMappedData
                ) => {
                    const isBlocked =
                        !!(service.service?.paymentDisabled || serviceCharge.isPayBlocked);

                    existedBalance.periodYear = serviceCharge.periodYear;
                    existedBalance.periodMonth = serviceCharge.periodMonth;

                    if (!isBlocked) {
                        existedBalance.beginPeriodBalance += serviceCharge.beginPeriodBalance ?? 0;
                        existedBalance.paymentsAmount += serviceCharge.paymentsAmount ?? 0;
                        existedBalance.endPeriodBalance += serviceCharge.endPeriodBalance ?? 0;
                        existedBalance.recalcAmount += serviceCharge.recalcAmount;

                        for (const chargeDetail of serviceCharge.accountServiceChargeDetails) {
                            existedBalance.chargeAmount += chargeDetail.chargeAmount ?? 0;
                        }
                    }

                    for (
                        const chargeDetail of serviceCharge.accountServiceChargeDetails as Required<AccountServiceChargeDetailViewModel>[]
                    ) {
                        serviceMappedData.chargeAmount += chargeDetail.chargeAmount;
                        serviceMappedData.tariffPrice += chargeDetail.tariffPrice;
                    }
                };

                const measure = service?.accountServiceCharges[0]?.measure || '';

                if (existedServiceMappedData) {
                    existedServiceMappedData.beginPeriodBalance += serviceCharge.beginPeriodBalance;
                    existedServiceMappedData.paymentsAmount += serviceCharge.paymentsAmount;
                    existedServiceMappedData.endPeriodBalance += serviceCharge.endPeriodBalance;
                    existedServiceMappedData.chargeDetails.push(
                        ...mapAccountServiceChargeDetails(
                            serviceCharge, serviceCharge.accountServiceChargeDetails, measure, !!service.service?.hasTariff
                        )
                    );

                    extendServiceMappedDataFromChargeDetails(existedServiceMappedData);
                } else {
                    const isPaymentDisabled = !!(service.service?.paymentDisabled || serviceCharge?.isPayBlocked);

                    const newServiceMappedData: BalanceAndChargesServiceMappedData = {
                        id: service.service?.id ?? '',
                        name: `${service.service?.name}` || '(Неизвестная услуга)',
                        parentalServiceId: service.service?.parentalServiceId ?? '',
                        branch: service.branch as Branches,
                        accountServiceId: !isPaymentDisabled ? serviceCharge.accountServiceId : uniqueId('service-'),
                        beginPeriodBalance: serviceCharge.beginPeriodBalance,
                        paymentsAmount: serviceCharge.paymentsAmount,
                        endPeriodBalance: serviceCharge.endPeriodBalance,
                        chargeDetails: mapAccountServiceChargeDetails(
                            serviceCharge, serviceCharge.accountServiceChargeDetails, measure, !!service.service?.hasTariff
                        ),
                        chargeAmount: 0,
                        recalcAmount: serviceCharge.recalcAmount,
                        tariffPrice: 0,
                        debtType: serviceCharge.debtType,
                        hasTariff: !!service.service?.hasTariff,
                        excessPay: serviceCharge.excessPay,
                        increaseCoefficient: serviceCharge.increaseCoefficient,
                        // #2162 если флаг опциональный, тогда считаем что по умолчанию можно платить
                        prepaymentAllowed: !account?.isDisabled && (service.service?.prepaymentAllowed ?? true),
                        isAtomic: isServiceFromAtomicGroup,
                        codeGroup: !!service.performer ? service.performer?.codeGroup : service.provider?.codeGroup,
                        priority: service.service?.priority ?? SERVICE_PRIORITY_DEFAULT_NULL_VALUE,
                        contragentPriority: service.contragent?.priority ?? 0,
                        paymentDisabled: isPaymentDisabled,
                        periodMonth: serviceCharge.periodMonth,
                        periodYear: serviceCharge.periodYear,
                        isChargeDecoding,
                    };

                    extendServiceMappedDataFromChargeDetails(newServiceMappedData);

                    servicesGroupMappedData.push(newServiceMappedData);
                    servicesAllMappedData[providerName].push(newServiceMappedData);
                }
            }
        }
    }

    // сортировка услуг по приоритетам

    const sorteredGroupedData: Record<number, AccountWithMappedBalanceAndChargesData> = {};

    Object.keys(groupedData).forEach((groupedDataKey: string) => {
        const sorteredMappedData: Partial<Record<DebtTypeFromBilling, BalanceAndChargesMappedDataItem>> = {};
        // @ts-expect-error Why NUMBER
        const groupedMappedData = groupedData[groupedDataKey].mappedData as BalanceAndChargesMappedData;
        if (!!groupedMappedData) {
            Object.keys(groupedMappedData).forEach((mappedDataItemKey) => {

                // сортировка allServices по приоритету контрагента
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.allServices) {
                    // @ts-expect-error no error
                    groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].allServices = Object.fromEntries(
                        // @ts-expect-error no error
                        Object.entries(groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].allServices).sort((a, b) => {
                            // приоритет контрагента вшит в сервис, из-за структуры объекта
                            const priorityA = a[1]?.[0].contragentPriority ?? 0;
                            const priorityB = b[1]?.[0].contragentPriority ?? 0;

                            return priorityB - priorityA;
                        })
                    );
                }

                // сортировка atomicServices по приоритету контрагента
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.atomicServices) {
                    // @ts-expect-error no error
                    groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].atomicServices = Object.fromEntries(
                        // @ts-expect-error no error
                        Object.entries(groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].atomicServices).sort((a, b) => {
                            // приоритет контрагента вшит в сервис, из-за структуры объекта
                            const priorityA = a[1]?.[0].contragentPriority ?? 0;
                            const priorityB = b[1]?.[0].contragentPriority ?? 0;

                            return priorityB - priorityA;
                        })
                    );
                }

                // сортировка separatableServices по приоритету контрагента
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.separatableServices) {
                    // @ts-expect-error no error
                    groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].separatableServices = Object.fromEntries(
                        // @ts-expect-error no error
                        Object.entries(groupedMappedData[mappedDataItemKey as DebtTypeFromBilling].separatableServices).sort((a, b) => {
                            // приоритет контрагента вшит в сервис, из-за структуры объекта
                            const priorityA = a[1]?.[0].contragentPriority ?? 0;
                            const priorityB = b[1]?.[0].contragentPriority ?? 0;

                            return priorityB - priorityA;
                        })
                    );
                }

                // сортировка allServices по приоритету
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.allServices) {
                    // @ts-expect-error no error
                    Object.keys((groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]).allServices).forEach(serviceKey => {
                        groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.allServices?.[serviceKey].sort((a, b) => {
                            return a.priority - b.priority;
                        });
                    });
                }

                // сортировка atomicServices по приоритету
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.atomicServices) {
                    // @ts-expect-error no error
                    Object.keys((groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]).atomicServices).forEach(serviceKey => {
                        groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.atomicServices?.[serviceKey].sort((a, b) => {
                            return a.priority - b.priority;
                        });
                    });
                }

                // сортировка separatableServices по приоритету
                if (!!groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.separatableServices) {
                    // @ts-expect-error no error
                    Object.keys((groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]).separatableServices).forEach(serviceKey => {
                        groupedMappedData[mappedDataItemKey as DebtTypeFromBilling]?.separatableServices?.[serviceKey].sort((a, b) => {
                            return a.priority - b.priority;
                        });
                    });
                }

                sorteredMappedData[mappedDataItemKey as DebtTypeFromBilling] = {
                    ...(groupedMappedData[mappedDataItemKey as DebtTypeFromBilling] as BalanceAndChargesMappedDataItem),
                };
            });
        }

        // @ts-expect-error Why NUMBER
        sorteredGroupedData[groupedDataKey] = { ...groupedData[groupedDataKey], mappedData: { ...sorteredMappedData, } };
    });

    return {
        ..._.clone(dataToClone), mappedData, groupedData: sorteredGroupedData, services
    };
};
